import { useMemo } from 'react';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { LOCAL, createEntry } from '@atlassian/jira-cache/src/index.tsx';
import { getUserProperty, setUserProperty } from '../common/user-properties/index.tsx';
import type { RECOMMENDATION_PRODUCT_KEYS } from '../recommendations/index.tsx';

const VISITED_COUNT_KEY = 'CROSS_JOIN_NUDGE_CONFLUENCE_VISITED_COUNT';
export const VISITED_CONFLUENCE_LLM_COUNT_KEY = 'CROSS_JOIN_NUDGE_CONFLUENCE_LLM_VISITED_COUNT';
const ONE_DAY_IN_MILLISECONDS = 86400000;
export const crossJoinNudgeVisitedCache = createLocalStorageProvider('cross-join-nudge-visited');

const visitedKeys = {
	crossJoinNudgesConfluence: 'cross.join.nudges.confluence.visited',
	crossJoinNudgesJiraToJpd: 'cross.join.nudges.jpd.visited',
	crossJoinNudgesJiraToLoom: 'cross.join.nudges.loom.visited',
	crossJoinNudgesYourWork: 'cross.join.nudges.your.work',
	crossJoinNudgesLLMAA: 'cross.join.nudges.llm.aa',
	crossJoinNudgesMsr: 'cross.join.nudges.msr',
} as const;
type CacheKeyName = keyof typeof visitedKeys;

export type EligibilityEventCacheValue = { isEligible: boolean; reason: string };
export type EligibilityEventCacheKey =
	(typeof RECOMMENDATION_PRODUCT_KEYS)[keyof typeof RECOMMENDATION_PRODUCT_KEYS];

type EligibilityCacheValue = { value: boolean; ttl?: number };
type EligibilityCacheKey =
	| 'user_already_has_jpd'
	| 'user_cannot_request_jpd_access'
	| 'jpd_is_too_new'
	| 'user_is_too_new'
	| 'confluence_is_too_new'
	| 'user_already_has_confluence'
	| 'user_cannot_request_confluence_access'
	| 'site_is_opted_out'
	| 'has_collaborators'
	| 'has_seen_conf_llm'
	| 'has_seen_jpd'
	| 'has_seen_conf'
	| 'has_enrolled_msr_v1'
	| 'user_new_for_llm_exp_v2'
	| 'loom_site_is_more_than_7_days_old'
	| 'user_is_too_new_for_loom'
	| 'user_already_has_loom_access'
	| 'user_can_request_loom_access'
	| 'has_seen_nudge_in_last_7_days';

export const cjnEventEligibilityCache = createEntry<EligibilityEventCacheValue>({
	size: 30,
	cacheKey: 'cjn-event-eligibility-cache',
	storageType: LOCAL,
});

/**
 * Do not use directly except in tests, instead use cjnEligibilityCacheHandler
 */
export const unsafeCjnEligibilityCache = createEntry<EligibilityCacheValue>({
	size: 30,
	cacheKey: 'cjn-eligibility-cache',
	storageType: LOCAL,
});

export const cjnEligibilityCacheHandler = {
	get: (key: EligibilityCacheKey) => unsafeCjnEligibilityCache.get(key),
	set: (key: EligibilityCacheKey, value: EligibilityCacheValue) =>
		unsafeCjnEligibilityCache.set(key, value),
	remove: (key: EligibilityCacheKey) => unsafeCjnEligibilityCache.remove(key),
};

export function withEligibilityCache<A extends unknown[]>(
	key: EligibilityCacheKey,
	eligibilityCheck: (...args: A) => Promise<boolean>,
	daysToLive?: number,
) {
	return async (...args: A): Promise<boolean> => {
		try {
			const cachedValue = await cjnEligibilityCacheHandler.get(key);
			if (cachedValue !== null && cachedValue !== undefined) {
				if (!cachedValue.ttl || cachedValue.ttl > Date.now()) {
					return cachedValue.value;
				}
			}
		} catch (e) {
			// cache get fail, do nothing
		}
		const result = await eligibilityCheck(...args);

		try {
			const ttlTimestamp =
				daysToLive !== undefined
					? new Date().getTime() + ONE_DAY_IN_MILLISECONDS * daysToLive
					: undefined;
			await cjnEligibilityCacheHandler.set(key, { value: result, ttl: ttlTimestamp });
		} catch (e) {
			// cache set fail, do nothing
		}
		return result;
	};
}

export const isLocalStorageEnabled = async () => {
	await unsafeCjnEligibilityCache.set('local_storage_enabled', { value: true });
	const value = await unsafeCjnEligibilityCache.get('local_storage_enabled');
	return value?.value === true;
};

export const getOrUpdateFeatureVisited = (
	keyName: CacheKeyName,
	snoozeForDays: number,
): boolean => {
	try {
		if (getFeatureVisited(keyName)) {
			return true;
		}
		setFeatureVisited(keyName, snoozeForDays);

		// A small cohort of users have local storage completely disabled.
		// This code detects that and always returns visited=true for those users
		if (!getFeatureVisited(keyName)) {
			fireErrorAnalytics({
				meta: {
					id: 'featureVisitedLocalStorageNotAvailabe',
					packageName: 'jiraCrossJoinNudges',
				},
			});
			return true;
		}
		return false;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'useFeatureVisited',
				packageName: 'jiraCrossJoinNudges',
			},
			error,
			skipSentry: true,
			sendToPrivacyUnsafeSplunk: true,
		});
		return true;
	}
};

export const removeFeatureVisited = (keyName: CacheKeyName): void => {
	const key = visitedKeys[keyName];
	crossJoinNudgeVisitedCache.remove(key);
};

export const setFeatureVisited = (keyName: CacheKeyName, numOfDaysSnoozed: number): void => {
	const key = visitedKeys[keyName];
	const SNOOZE_UNTIL_TIMESTAMP = new Date().getTime() + ONE_DAY_IN_MILLISECONDS * numOfDaysSnoozed;
	crossJoinNudgeVisitedCache.set(key, SNOOZE_UNTIL_TIMESTAMP);
};

export const snoozeFeatureFor7Days = (keyName: CacheKeyName): void => {
	setFeatureVisited(keyName, 7);
};

export const snoozeFeatureFor30Days = (keyName: CacheKeyName): void => {
	setFeatureVisited(keyName, 30);
};

export const getFeatureVisited = (keyName: CacheKeyName): boolean => {
	const key = visitedKeys[keyName];
	const snoozeUntilTimestampString = crossJoinNudgeVisitedCache.get(key);

	if (snoozeUntilTimestampString !== null) {
		const SNOOZE_UNTIL_TIMESTAMP = parseInt(snoozeUntilTimestampString, 10);
		const TIMESTAMP = new Date().getTime();
		if (!Number.isNaN(SNOOZE_UNTIL_TIMESTAMP) && TIMESTAMP < SNOOZE_UNTIL_TIMESTAMP) {
			return true;
		}
	}
	return false;
};

export const getFeatureVisitedCount = async (
	accountId: string,
	propertyKey: string = VISITED_COUNT_KEY,
): Promise<number> => {
	if (!accountId) return 0;

	const visitedCount = await getUserProperty<number>({
		accountId,
		propertyKey,
		defaultValue: 0,
	});

	return !Number.isNaN(visitedCount.value) ? visitedCount.value : 0;
};

export const increaseFeatureVisitedCount = async (
	accountId: string,
	incrementedCount: number,
	propertyKey = VISITED_COUNT_KEY,
) => {
	await setUserProperty<number>({
		accountId,
		propertyKey,
		propertyValue: incrementedCount,
	});
};

/**
 * @function getCountCacheKey computes the key for the user properties API
 * historically, consumers of @function useSnooze use computed keys
 * while direct users of the api like in @function increaseFeatureVisitedCount do not use computed keys.
 * Verify if you need a computed or non-computed key before using
 */
export const getCountCacheKey = (keyName: CacheKeyName) => {
	const key = visitedKeys[keyName];
	return `${key}.count`;
};

export const useSnooze = (keyName: CacheKeyName) => {
	const accountId = useAccountId() || '';
	const countKey = getCountCacheKey(keyName);
	return useMemo(
		() => ({
			snoozeFor7Days: () => snoozeFeatureFor7Days(keyName),
			snoozeFor30Days: () => snoozeFeatureFor30Days(keyName),
			getVisitedCount: () => getFeatureVisitedCount(accountId, countKey),
			increaseVisitedCount: (incrementedCount: number) =>
				increaseFeatureVisitedCount(accountId, incrementedCount, countKey),
			isVisited: () => getFeatureVisited(keyName),
		}),
		[accountId, countKey, keyName],
	);
};

export const setHasSeenNudgeInLast7Days = async (): Promise<void> => {
	const DAYS_TO_CHECK = 7;

	try {
		// overwrite the key even if it exists
		await cjnEligibilityCacheHandler.set('has_seen_nudge_in_last_7_days', {
			value: true,
			ttl: ONE_DAY_IN_MILLISECONDS * DAYS_TO_CHECK + Date.now(),
		});
	} catch (e) {
		// cache set fail, do nothing
	}
};

export const getHasSeenNudgeInLast7Days = async (): Promise<boolean> => {
	try {
		const result = await cjnEligibilityCacheHandler.get('has_seen_nudge_in_last_7_days');
		if (result?.ttl && result.ttl > Date.now()) {
			return true;
		}
	} catch (e) {
		// cache get fail, return false
	}
	return false;
};
